import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { currentApiBaseUrl } from "@/lib/env";
import useDebugPanelSchema, {
  DebugPanelFormValues,
} from "../hooks/use-debug-panel-schema";
import {
  API_NAMESPACE_MAX_LENGTH,
  readApiNamespace,
  switchApiNamespace,
} from "../utils/api-namespace";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showApiNamespace: boolean;
}

const storedValues = (): DebugPanelFormValues => ({
  apiNamespace: readApiNamespace() ?? "",
});

const DebugPanelDialog: FC<Props> = ({
  open,
  onOpenChange,
  showApiNamespace,
}) => {
  const { t } = useTranslation(["debug-panel"]);
  const schema = useDebugPanelSchema();

  const form = useForm<DebugPanelFormValues>({
    resolver: zodResolver(schema),
    defaultValues: storedValues(),
  });

  // Edits left unsaved when the dialog was closed do not come back with it.
  useEffect(() => {
    if (open) {
      form.reset(storedValues());
    }
  }, [open, form]);

  // The HTTP client takes its base once, at startup, so a new backend needs a
  // fresh page; an unchanged one just closes the dialog.
  const applyNamespace = (name: string | null) => {
    if (switchApiNamespace(name)) {
      window.location.reload();
      return;
    }

    onOpenChange(false);
  };

  const onSubmit = ({ apiNamespace }: DebugPanelFormValues) =>
    applyNamespace(apiNamespace || null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>{t("debug-panel:title", "Debug panel")}</DialogTitle>
          <DialogDescription>
            {t(
              "debug-panel:description",
              "Applies to this browser tab only. Changing the API namespace reloads the page and starts a new session.",
            )}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-6"
            noValidate
          >
            {showApiNamespace ? (
              <FormField
                control={form.control}
                name="apiNamespace"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("debug-panel:api-namespace-label", "API namespace")}
                    </FormLabel>
                    <div className="flex items-center gap-1 text-sm">
                      <span className="text-muted-foreground">api.dev-</span>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="agafonov"
                          maxLength={API_NAMESPACE_MAX_LENGTH}
                          autoComplete="off"
                          autoCapitalize="none"
                          spellCheck={false}
                        />
                      </FormControl>
                      <span className="text-muted-foreground">
                        .follosoft.com
                      </span>
                    </div>
                    <FormDescription>
                      {t(
                        "debug-panel:api-namespace-current",
                        "Requests now go to:",
                      )}{" "}
                      <code className="break-all">{currentApiBaseUrl()}</code>
                      <br />
                      {t(
                        "debug-panel:api-namespace-empty-hint",
                        "Leave empty for the default API.",
                      )}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                {t(
                  "debug-panel:no-settings",
                  "No debug settings are available on this environment.",
                )}
              </p>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => applyNamespace(null)}
              >
                {t("debug-panel:reset", "Reset")}
              </Button>
              <Button type="submit" disabled={!showApiNamespace}>
                {t("debug-panel:save", "Save")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default DebugPanelDialog;
